import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig, AxiosError } from 'axios';

// Storage keys (debe coincidir con AuthContext)
const TOKEN_KEY = 'erp_access_token';
const REFRESH_TOKEN_KEY = 'erp_refresh_token';
const COMPANY_ID_KEY = 'erp_company_id';

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag para evitar multiples refreshes simultaneos
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Inject X-Company-ID header for protected routes (all except /auth and /health)
    const isPublicRoute = 
      config.url?.includes('/auth/login') ||
      config.url?.includes('/auth/register') ||
      config.url?.includes('/auth/refresh') ||
      config.url?.includes('/health');
    
    if (!isPublicRoute) {
      const companyIdStr = localStorage.getItem(COMPANY_ID_KEY);
      // Convert to int, use 1 as default
      const companyId = companyIdStr ? parseInt(companyIdStr, 10) : 1;
      if (config.headers) {
        config.headers['X-Company-ID'] = String(companyId); // Send as string but parsed from int
        if (config.url && !config.url.includes('/companies')) {
          console.log('[v0] API Request - Setting X-Company-ID header:', { 
            url: config.url, 
            companyId, 
            companyIdStr,
            header: config.headers['X-Company-ID']
          });
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Si es 401 y no es el endpoint de login/refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        // Si ya hay un refresh en progreso, encolar la peticion
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!refreshToken) {
        // No hay refresh token, limpiar y redirigir
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post('/api/v1/auth/refresh', {
          refreshToken,
        });

        const { accessToken } = response.data.data;
        localStorage.setItem(TOKEN_KEY, accessToken);

        processQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Refresh fallido, limpiar y redirigir
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Otros errores
    if (error.response) {
      switch (error.response.status) {
        case 403:
          console.error('Acceso denegado');
          break;
        case 500:
          console.error('Error del servidor');
          break;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Helper functions for common HTTP methods
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) => 
    apiClient.get<T>(url, { params }).then((res) => res.data),
  
  post: <T>(url: string, data?: unknown) => 
    apiClient.post<T>(url, data).then((res) => res.data),
  
  put: <T>(url: string, data?: unknown) => 
    apiClient.put<T>(url, data).then((res) => res.data),
  
  patch: <T>(url: string, data?: unknown) => 
    apiClient.patch<T>(url, data).then((res) => res.data),
  
  delete: <T>(url: string, data?: unknown) =>
    apiClient.delete<T>(url, data !== undefined ? { data } : undefined).then((res) => res.data),

  getBlob: (url: string) =>
    apiClient.get(url, { responseType: 'blob' }).then((res) => res.data as Blob),
};
